import { SpecialtyCountryUnsupportedError } from '@/hooks/query/specialty-country-unsupported-error';
import { cn } from '@/lib/cn';
import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { TypographyP2 } from '@heidi/design-system/typography';
import { countries } from '@heidi/utils/countries';
import { FormattedMessage } from 'react-intl';

interface SpecialtiesLoadErrorProps {
  /** The specialties query error — a 422 country rejection gets a specific message. */
  error: unknown;
  className?: string;
}

/**
 * Inline fail-loud error for a failed specialties fetch. Shared by every
 * specialty picker so the copy stays a single translation unit per case.
 */
export function SpecialtiesLoadError({ error, className }: SpecialtiesLoadErrorProps) {
  const unsupportedCode =
    error instanceof SpecialtyCountryUnsupportedError ? error.country : undefined;
  const unsupportedCountryName = unsupportedCode
    ? (countries.find((c) => c.countryCode === unsupportedCode)?.name ?? unsupportedCode)
    : undefined;

  return (
    <TypographyP2 className={cn('text-validation-destructive', className)}>
      {unsupportedCountryName ? (
        isHeidiSupportReachable ? (
          <FormattedMessage
            defaultMessage="Country {country} is not supported. Please contact support."
            id="CxJOpjRG7W"
            values={{ country: unsupportedCountryName }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="Country {country} is not supported. Please contact your IT team."
            id="3ZB2g9/fVx"
            values={{ country: unsupportedCountryName }}
          />
        )
      ) : isHeidiSupportReachable ? (
        <FormattedMessage
          defaultMessage="We couldn't load specialties. Please try again later or contact support."
          id="32CjTCeIAE"
        />
      ) : (
        <FormattedMessage
          defaultMessage="We couldn't load specialties. Please try again later or contact your IT team."
          id="4VV6HXJ609"
        />
      )}
    </TypographyP2>
  );
}
